export type PickerRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

const EDGE = 12;
const GAP = 9;
const PANEL_PADDING = 12;
const CARD_MAX_WIDTH = 204;
const CARD_MAX_HEIGHT = 238;

export type PickerLayout = {
  width: number;
  estimatedHeight: number;
};

/** Four bare image choices in one row, each cropped to the target slot ratio. */
export function pickerLayout(aspectRatio: number, availableWidth: number, availableHeight = 800): PickerLayout {
  const safeRatio = Math.max(aspectRatio, 0.2);
  const widthAvailablePerCard = Math.max(0, (availableWidth - PANEL_PADDING * 2 - GAP * 3) / 4);
  const heightAvailable = Math.max(0, availableHeight - PANEL_PADDING * 2);
  const cardWidth = Math.min(CARD_MAX_WIDTH, widthAvailablePerCard, safeRatio * Math.min(CARD_MAX_HEIGHT, heightAvailable));
  return {
    width: Math.ceil(cardWidth * 4 + GAP * 3 + PANEL_PADDING * 2),
    estimatedHeight: Math.ceil(cardWidth / safeRatio + PANEL_PADDING * 2),
  };
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * Right-align the row to its trigger and open upward over the image. The image
 * is the preferred boundary; the slide is the hard boundary when a very small
 * target cannot contain the picker.
 */
export function positionPicker(
  trigger: PickerRect,
  picker: Pick<PickerRect, 'width' | 'height'>,
  boundary: PickerRect,
  target: PickerRect,
): { left: number; top: number } {
  const boundaryLeft = boundary.left + EDGE;
  const boundaryRight = boundary.right - EDGE;
  const boundaryTop = boundary.top + EDGE;
  const boundaryBottom = boundary.bottom - EDGE;

  const targetLeft = Math.max(boundaryLeft, target.left);
  const targetRight = Math.min(boundaryRight, target.right);
  const targetTop = Math.max(boundaryTop, target.top);
  const targetBottom = Math.min(boundaryBottom, target.bottom);
  const targetCanContainWidth = targetRight - targetLeft >= picker.width;
  const targetCanContainHeight = targetBottom - targetTop >= picker.height;

  const left = clamp(
    trigger.right - picker.width,
    targetCanContainWidth ? targetLeft : boundaryLeft,
    (targetCanContainWidth ? targetRight : boundaryRight) - picker.width,
  );

  const verticalTop = targetCanContainHeight ? targetTop : boundaryTop;
  const verticalBottom = targetCanContainHeight ? targetBottom : boundaryBottom;
  const above = trigger.top - GAP - picker.height;
  const below = trigger.bottom + GAP;
  const top = above >= verticalTop
    ? above
    : below + picker.height <= verticalBottom
      ? below
      : clamp(above, verticalTop, verticalBottom - picker.height);
  return { left, top };
}
